/**
 * Shared GA4 bot / automation detection.
 *
 * Source of truth for both the early inline gate in index.html (injected
 * at build/dev by vite-plugin-ga-bot-gate) and the later React init path.
 * Do not duplicate the regex list elsewhere.
 */

declare global {
  interface Window {
    __gaBlocked?: boolean;
  }
}

/** Same patterns as PR #8 — do not expand without a separate decision. */
export const BOT_UA_PATTERNS: RegExp[] = [
  /HeadlessChrome/i,
  /PhantomJS/i,
  /Puppeteer/i,
  /Playwright/i,
  /python-requests/i,
  /curl\//i,
  /Go-http-client/i,
  /axios\//i,
  /PostmanRuntime/i,
  /node-fetch/i,
  /Wget/i,
  /Scrapy/i,
  /Selenium/i,
];

/**
 * Detect headless browsers, automation frameworks and scripted clients
 * that GA4's built-in crawler filter does not catch.
 */
export const isLikelyBot = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  // Frameworks de automação definem esta flag
  if (navigator.webdriver) return true;

  const ua = navigator.userAgent || '';
  return BOT_UA_PATTERNS.some((pattern) => pattern.test(ua));
};

export const isGaBlocked = (): boolean =>
  typeof window !== 'undefined' && window.__gaBlocked === true;

export const applyGaBotGate = (): boolean => {
  const blocked = isLikelyBot();
  if (typeof window !== 'undefined') {
    window.__gaBlocked = blocked;
  }
  return blocked;
};

/**
 * Classic IIFE for a synchronous <script> in index.html <head>.
 * Must match isLikelyBot() bit-for-bit (same webdriver check + same regexes).
 */
export const getGaBotGateInlineScript = (): string => {
  const patternLiterals = BOT_UA_PATTERNS.map(
    (pattern) => `/${pattern.source}/${pattern.flags}`
  ).join(',\n      ');

  return `(function () {
    try {
      if (typeof navigator === 'undefined') {
        window.__gaBlocked = false;
        return;
      }
      if (navigator.webdriver) {
        window.__gaBlocked = true;
        return;
      }
      var ua = navigator.userAgent || '';
      var botPatterns = [
      ${patternLiterals}
      ];
      for (var i = 0; i < botPatterns.length; i++) {
        if (botPatterns[i].test(ua)) {
          window.__gaBlocked = true;
          return;
        }
      }
      window.__gaBlocked = false;
    } catch (e) {
      window.__gaBlocked = false;
    }
  })();`;
};
